import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import { usePostTestChatMessage } from 'actions/chatbot';
import MessageBubble from './message-bubble';
import {
  ChatContainer,
  MessagesArea,
  MessagesSpacer,
  InputRow,
  ChatInput,
  SendButton,
  TypingIndicator,
} from './styled-comps';

function ChatArea({ ticket }) {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const { post: postMessage } = usePostTestChatMessage({
    onComplete: (errorMessage, data) => {
      setLoading(false);
      if (errorMessage) {
        dispatch(
          showNotification({
            text: 'Failed to send message',
            duration: 5,
            showCloseIcon: true,
          }),
        );
        appendBotMessage({
          content: 'Error: failed to get response',
          message_type: 'TEXT',
        });
      } else {
        appendBotMessage(data);
      }
    },
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = useCallback(
    (content, selectedDisposition) => {
      if (!ticket?.id) return;

      setLoading(true);
      postMessage({
        ticket_id: ticket.id,
        content: content || '',
        selected_disposition: selectedDisposition || undefined,
      });
    },
    [ticket, postMessage],
  );

  const appendBotMessage = botResponse => {
    setMessages(prev => [
      ...prev,
      {
        id: `bot-${Date.now()}`,
        content: botResponse.content || '',
        sender: 'bot',
        sender_type: 'BOT',
        message_type: botResponse.message_type,
        structured: buildStructured(botResponse),
        debug: botResponse.debug_trace || null,
      },
    ]);
  };

  const buildStructured = response => {
    const data = response.structured_data;
    if (!data || !data.options) return null;
    if (response.message_type === 'BUTTONS') {
      return { type: 'BUTTONS', buttons: data.options };
    }
    return null;
  };

  // Send initial empty message to get greeting on mount
  useEffect(() => {
    if (ticket?.id) {
      sendMessage('', null);
    }
  }, [ticket?.id, sendMessage]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    setMessages(prev => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        content: trimmed,
        sender: 'customer',
        sender_type: 'CUSTOMER',
      },
    ]);

    setInput('');
    sendMessage(trimmed, null);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const inputRef = useRef(null);

  const handleAction = action => {
    const dispositionPath = action.structured_data?.selected_disposition;
    const freeTextAction = action.structured_data?.action === 'free_text';

    // "Let me type instead" -- just focus the input
    if (
      freeTextAction ||
      (!dispositionPath && action.content === 'Let me type instead')
    ) {
      inputRef.current?.focus();
      return;
    }

    const displayText = action.content || dispositionPath || '';

    setMessages(prev => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        content: displayText,
        sender: 'customer',
        sender_type: 'CUSTOMER',
      },
    ]);

    if (dispositionPath) {
      sendMessage('', dispositionPath);
    } else {
      sendMessage(displayText, null);
    }
  };

  return (
    <ChatContainer>
      <MessagesArea>
        <MessagesSpacer />
        {messages.map(msg => (
          <MessageBubble key={msg.id} message={msg} onAction={handleAction} />
        ))}
        <div ref={messagesEndRef} />
      </MessagesArea>
      {loading && <TypingIndicator>Bot is thinking...</TypingIndicator>}
      <InputRow>
        <ChatInput
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={loading}
          maxLength={2000}
        />
        <SendButton onClick={handleSend} disabled={!input.trim() || loading}>
          Send
        </SendButton>
      </InputRow>
    </ChatContainer>
  );
}

export default ChatArea;
